import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../Style/BatteryDashboard.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";

const defaultVehicleData = {
  vehicle_id: null,
  customer_id: null,
  vin: null,
  powertrain: null,
  vehicle_type: null,
  registration_type: null,
  range_km: null,
  operating_hours: null,
  is_active: null,
  registered_on: null,
};

const defaultBatteryMetrics = {
  current: null,
  fault: { error_code: null, level: null },
  soc: null,
  soh: null,
  sop: null,
  state: null,
  temperature: null,
  voltage: null,
};

const createEmptyCells = () =>
  Array.from({ length: 4 }, (_, i) => ({
    id: i + 1,
    status: "empty",
    temperature: 0,
    voltage: 0,
  }));

const BatteryHeader = ({ batteryMetrics, lastUpdate }) => {
  return (
    <div className="header-Battery">
      <div className="header-stats-Battery">
        <span className="header-stat-item-Battery">
          <span>Current:</span>
          <span className="green-text-Battery">
            {batteryMetrics.current ?? "NA"} A
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>Fault Level:</span>
          <span className="red-Battery">
            {batteryMetrics.fault?.level ?? "NA"}
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>Error Code:</span>
          <span className="red-Battery">
            {batteryMetrics.fault?.error_code ?? "NA"}
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>SoC:</span>
          <span className="green-text-Battery">
            {batteryMetrics.soc ?? "NA"}%
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>SoH:</span>
          <span className="green-text-Battery">
            {batteryMetrics.soh ?? "NA"}%
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>SoP:</span>
          <span className="green-text-Battery">
            {batteryMetrics.sop ?? "NA"}%
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>State:</span>
          <span className="Black-text-Battery">
            {batteryMetrics.state ?? "NA"}
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>Temp:</span>
          <span className="green-text-Battery">
            {batteryMetrics.temperature ?? "NA"}°C
          </span>
        </span>
        <span className="header-stat-item-Battery">
          <span>Voltage:</span>
          <span className="green-text-Battery">
            {batteryMetrics.voltage ?? "NA"} V
          </span>
        </span>
      </div>
      <div className="header-icons-Battery">
        <span className="last-update-Battery">Last Update: {lastUpdate}</span>
      </div>
    </div>
  );
};

const VehicleDetails = ({ vehicleData, selectedVehicleId }) => {
  if (!selectedVehicleId) {
    return (
      <div className="selected-vehicle-details-Battery">
        <h2 className="section-title-Battery">Vehicle Details</h2>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Select Vehicle
        </div>
      </div>
    );
  }

  return (
    <div className="selected-vehicle-details-Battery">
      <h2 className="section-title-Battery">
        Vehicle Details: {vehicleData.vehicle_id ?? "NA"}
      </h2>
      <div className="details-grid-Battery">
        <div className="detail-item-Battery">
          <span>Customer ID:</span> {vehicleData.customer_id ?? "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>Vehicle ID:</span> {vehicleData.vehicle_id ?? "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>VIN:</span> {vehicleData.vin ?? "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>Powertrain:</span> {vehicleData.powertrain ?? "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>Vehicle Type:</span> {vehicleData.vehicle_type ?? "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>Registration Type:</span>{" "}
          {vehicleData.registration_type ?? "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>Range:</span> {vehicleData.range_km ?? "NA"} km
        </div>
        <div className="detail-item-Battery">
          <span>Operating Hours:</span> {vehicleData.operating_hours ?? "NA"}{" "}
          hrs
        </div>
        <div className="detail-item-Battery">
          <span>Is Active:</span>{" "}
          {vehicleData.is_active !== null
            ? vehicleData.is_active
              ? "Yes"
              : "No"
            : "NA"}
        </div>
        <div className="detail-item-Battery">
          <span>Registered On:</span>{" "}
          {vehicleData.registered_on
            ? new Date(vehicleData.registered_on).toLocaleString()
            : "NA"}
        </div>
      </div>
    </div>
  );
};

const BatteryCells = ({ batteryCells }) => {
  return (
    <div className="ups-test-list-Battery">
      <div className="ups-test-header-Battery">
        <h2 className="ups-test-title-Battery">UPSTest: BM1-Cell Monitoring</h2>
        <div className="battery-status-legend-Battery">
          <span className="legend-item-Battery">
            <span className="status-indicator-Battery green-bg-Battery"></span>
            Healthy
          </span>
          <span className="legend-item-Battery">
            <span className="status-indicator-Battery red-bg-Battery"></span>
            Error
          </span>
          <span className="legend-item-Battery">
            <span className="status-indicator-Battery gray-bg-Battery"></span>
            Offline
          </span>
        </div>
      </div>
      <div className="battery-grid-Battery">
        {batteryCells.map((cell) => {
          const status = String(cell.status || "").toLowerCase();
          const isEmpty = status === "empty";
          const isUnknown = status === "unknown";
          const isHealthy = status === "healthy";
          let cellClass = "gray-bg-Battery";

          if (isHealthy) {
            cellClass = "green-bg-Battery";
          } else if (isUnknown) {
            cellClass = "red-bg-Battery";
          } else if (isEmpty) {
            cellClass = "gray-bg-Battery";
          }

          return (
            <div key={cell.id} className={`battery-cell-Battery ${cellClass}`}>
              <div className="battery-cell-id">{cell.id}</div>
              <div className="battery-cell-value">
                {isEmpty ? 0 : (cell.temperature ?? 0)}°C
              </div>
              <div className="battery-cell-value">
                {isEmpty ? 0 : (cell.voltage ?? 0)}V
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HistoricalTrends = ({
  voltageData,
  currentData,
  temperatureData,
  selectedVehicleId,
}) => {
  const renderChart = (data, dataKey, name, fill) => {
    if (!selectedVehicleId) {
      return (
        <div className="chart-area-Battery">
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Select Vehicle
          </div>
        </div>
      );
    }

    if (!Array.isArray(data) || data.length === 0) {
      return (
        <div className="chart-area-Battery">
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Data is not available
          </div>
        </div>
      );
    }

    return (
      <div className="chart-area-Battery">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="time" className="chart-axis-text-Battery" />
            <YAxis className="chart-axis-text-Battery" />
            <Tooltip wrapperClassName="chart-tooltip-Battery" />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey={dataKey} fill={fill} name={name} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="historical-trends-Battery">
      <div className="historical-trends-header-Battery">
        <h2 className="historical-trends-title-Battery">Historical Trends</h2>
      </div>
      <div className="charts-container-Battery">
        <div className="chart-section-Battery">
          <div className="chart-info-Battery">
            <span className="chart-color-indicator-Battery blue-bg-Battery"></span>
            <span className="chart-label-Battery">Voltage (V)</span>
          </div>
          {renderChart(voltageData, "value", "Voltage", "#3B82F6")}
        </div>
        <div className="chart-section-Battery">
          <div className="chart-info-Battery">
            <span className="chart-color-indicator-Battery yellow-bg-Battery"></span>
            <span className="chart-label-Battery">Current (A)</span>
          </div>
          {renderChart(currentData, "value", "Current", "#F59E0B")}
        </div>
        <div className="chart-section-Battery">
          <div className="chart-info-Battery">
            <span className="chart-color-indicator-Battery red-bg-Battery"></span>
            <span className="chart-label-Battery">Temperature (°C)</span>
          </div>
          {renderChart(temperatureData, "value", "Temperature", "#EF4444")}
        </div>
      </div>
    </div>
  );
};

const BatteryDashboard = () => {
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [timeRange, setTimeRange] = useState("1h");
  const [vehicleData, setVehicleData] = useState(defaultVehicleData);
  const [batteryMetrics, setBatteryMetrics] = useState(defaultBatteryMetrics);
  const [batteryCells, setBatteryCells] = useState(createEmptyCells);
  const [voltageData, setVoltageData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [temperatureData, setTemperatureData] = useState([]);
  const [totalStringActiveAlerts] = useState([
    {
      status: "Warning",
      time: "01/07/2025, 12:17:26",
      alert: "String voltage deviation detected",
      source: "String 1",
    },
  ]);
  const [upsTestActiveAlerts] = useState([
    {
      status: "Critical",
      time: "01/07/2025, 12:20:26",
      alert: "Over impedance",
      source: "Battery 15",
      measure: "15.80 mΩ",
      boundary: "< 10.0",
    },
    {
      status: "Warning",
      time: "01/07/2025, 12:19:26",
      alert: "Temperature threshold exceeded",
      source: "Battery 32",
      measure: "38.20 °C",
      boundary: "< 35.0",
    },
  ]);
  const [lastUpdate, setLastUpdate] = useState("NA");

  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");
  const userRole = localStorage.getItem("userRole");
  const loggedInCustomerId = localStorage.getItem("customerid");
  const isSuperAdmin = userRole === "SuperAdmin";

  useEffect(() => {
    if (!isSuperAdmin && loggedInCustomerId) {
      setSelectedCustomer(loggedInCustomerId);
    } else if (isSuperAdmin) {
      setSelectedCustomer("");
    }
  }, [isSuperAdmin, loggedInCustomerId]);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!token || !isSuperAdmin) return;

      try {
        const response = await fetch(`${API_BASE_URL}/customers`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch customers");
        }

        const data = await response.json();

        if (Array.isArray(data)) {
          setCustomers(data);
        }
      } catch (error) {
        console.error("Fetch customers error:", error);
      }
    };

    fetchCustomers();
  }, [token, isSuperAdmin]);

  useEffect(() => {
    if (!selectedCustomer) {
      setVehicles([]);
      setSelectedVehicleId("");
      return;
    }

    const fetchVehicles = async () => {
      if (!token) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/vehiclereg/assigned?customer_id=${encodeURIComponent(selectedCustomer)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch vehicles");
        }

        const data = await response.json();

        if (data.Vehicle && Array.isArray(data.Vehicle)) {
          setVehicles(data.Vehicle);
        } else {
          setVehicles([]);
        }
      } catch (error) {
        console.error("Fetch vehicles error:", error);
        setVehicles([]);
      }
    };

    fetchVehicles();
  }, [selectedCustomer, token]);

  const resetDashboardData = () => {
    setVehicleData(defaultVehicleData);
    setBatteryMetrics(defaultBatteryMetrics);
    setBatteryCells(createEmptyCells());
    setVoltageData([]);
    setCurrentData([]);
    setTemperatureData([]);
    setLastUpdate("NA");
  };

  const handleCustomerChange = (e) => {
    setSelectedCustomer(e.target.value);
    setSelectedVehicleId("");
    resetDashboardData();
    setTimeRange("1h");
  };

  const handleVehicleChange = (e) => {
    const vehicleId = e.target.value;
    setSelectedVehicleId(vehicleId);
    resetDashboardData();
    setTimeRange("1h");
  };

  const updateDashboardResponse = (result) => {
    if (result.battery) {
      setBatteryMetrics(result.battery);
    }

    if (Array.isArray(result.cells)) {
      if (result.cells.length > 0) {
        setBatteryCells(result.cells);
      } else {
        setBatteryCells(createEmptyCells());
      }
    }

    if (result.vehicle) {
      setVehicleData(result.vehicle);
    }

    if (result.last_update) {
      setLastUpdate(
        new Date(result.last_update).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      );
    }

    if (result.historical_trends) {
      setVoltageData(result.historical_trends.voltage || []);
      setCurrentData(result.historical_trends.current || []);
      setTemperatureData(result.historical_trends.temperature || []);
    } else {
      setVoltageData([]);
      setCurrentData([]);
      setTemperatureData([]);
    }
  };

  const fetchInitialDashboardData = useCallback(async () => {
    if (!selectedVehicleId) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/battery-dashboard/${selectedVehicleId}?range=1h`,
        {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Failed to retrieve battery dashboard data",
        );
      }

      updateDashboardResponse(result);
    } catch (err) {
      console.warn("API connection warning:", err);
    }
  }, [selectedVehicleId, token]);

  const fetchHistoricalData = useCallback(
    async (range) => {
      if (!selectedVehicleId) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/battery-dashboard/${selectedVehicleId}?range=${range}`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(
            result.message || "Failed to retrieve historical data",
          );
        }

        updateDashboardResponse(result);
      } catch (err) {
        console.warn("Historical data warning:", err);
      }
    },
    [selectedVehicleId, token],
  );

  useEffect(() => {
    if (!selectedVehicleId) return;
    fetchInitialDashboardData();
  }, [selectedVehicleId, fetchInitialDashboardData]);

  return (
    <div className="container-Battery">
      <BatteryHeader batteryMetrics={batteryMetrics} lastUpdate={lastUpdate} />
      <div className="main-content-Battery">
        <div className="dashboard-area-Battery">
          <div className="select-vehicle-section-Battery">
            <h2 className="section-title-Battery">Select Vehicle</h2>
            <div className="selection-row-horizontal-Battery">
              {isSuperAdmin && (
                <div className="dropdown-container-Battery">
                  <label className="dropdown-label-Battery">Customer ID:</label>
                  <select
                    className="vehicle-dropdown-Battery"
                    value={selectedCustomer}
                    onChange={handleCustomerChange}
                  >
                    <option value="">Select Customer</option>
                    {customers.map((c) => {
                      const custId = c.CustomerID || c.customer_id;
                      const custName =
                        c.CustomerName || c.customer_name || custId;

                      return (
                        <option key={custId} value={custId}>
                          {custName}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              <div className="dropdown-container-Battery">
                <label className="dropdown-label-Battery">Vehicle ID:</label>
                <select
                  className="vehicle-dropdown-Battery"
                  value={selectedVehicleId}
                  onChange={handleVehicleChange}
                  disabled={!selectedCustomer}
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map((v) => {
                    const vehId = v.VehicleNumber || v.vehicle_id;

                    return (
                      <option key={vehId} value={vehId}>
                        {vehId}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="time-filters-Battery">
                {["1h", "6h", "7d"].map((range) => (
                  <button
                    key={range}
                    type="button"
                    className={`time-filter-button-Battery ${
                      timeRange === range ? "active-Battery" : ""
                    }`}
                    onClick={() => {
                      setTimeRange(range);
                      fetchHistoricalData(range);
                    }}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <VehicleDetails
            vehicleData={vehicleData}
            selectedVehicleId={selectedVehicleId}
          />
          <BatteryCells batteryCells={batteryCells} />
          <HistoricalTrends
            voltageData={voltageData}
            currentData={currentData}
            temperatureData={temperatureData}
            selectedVehicleId={selectedVehicleId}
          />
          <div className="alerts-section-Battery">
            <div className="total-string-alerts-Battery">
              <h2 className="alerts-title-Battery">Alert & Notification</h2>
              <div className="table-responsive-Battery">
                <table className="alerts-table-Battery">
                  <thead>
                    <tr className="table-header-row-Battery">
                      <th className="table-header-Battery">Status</th>
                      <th className="table-header-Battery">Time</th>
                      <th className="table-header-Battery">Alert</th>
                      <th className="table-header-Battery">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totalStringActiveAlerts.map((item, index) => (
                      <tr key={index} className="table-row-Battery">
                        <td className="table-cell-Battery">
                          <span className="status-badge-Battery warning-badge-Battery">
                            {item.status}
                          </span>
                        </td>
                        <td className="table-cell-Battery">{item.time}</td>
                        <td className="table-cell-Battery">{item.alert}</td>
                        <td className="table-cell-Battery">{item.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="ups-test-alerts-Battery">
              <h2 className="alerts-title-Battery">
                UPSTest: BM1-Cell Monitoring
              </h2>
              <div className="table-responsive-Battery">
                <table className="alerts-table-Battery">
                  <thead>
                    <tr className="table-header-row-Battery">
                      <th className="table-header-Battery">Status</th>
                      <th className="table-header-Battery">Time</th>
                      <th className="table-header-Battery">Alert</th>
                      <th className="table-header-Battery">Source</th>
                      <th className="table-header-Battery">Measure</th>
                      <th className="table-header-Battery">Boundary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upsTestActiveAlerts.map((item, index) => (
                      <tr key={index} className="table-row-Battery">
                        <td className="table-cell-Battery">
                          <span
                            className={`status-badge-Battery ${
                              item.status === "Critical"
                                ? "critical-badge-Battery"
                                : "warning-badge-Battery"
                            }`}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td className="table-cell-Battery">{item.time}</td>
                        <td className="table-cell-Battery">{item.alert}</td>
                        <td className="table-cell-Battery">{item.source}</td>
                        <td className="table-cell-Battery">{item.measure}</td>
                        <td className="table-cell-Battery">{item.boundary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatteryDashboard;
